import {
  Component,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { delay, filter, take } from 'rxjs/operators';
import { Subject } from 'rxjs';

import {
  Attachment,
  Constants,
  Property,
  PropertySearcher,
  PropertySearcherUser,
  PropertySearcherUserProfile,
  SelfDisclosureModel,
  SelfDisclosureQuestion,
  SelfDisclosureQuestionType
} from '@ui/shared/model';

import { TranslatePipe } from '@ngx-translate/core';
import { NgStyle } from '@angular/common';
import { SelfDisclosurePersonComponent } from '../self-disclosure-person/self-disclosure-person.component';
import { SelfDisclosurePersonListComponent } from '../self-disclosure-person-list/self-disclosure-person-list.component';
import { SelfDisclosureChildListComponent } from '../self-disclosure-child-list/self-disclosure-child-list.component';
import { SelfDisclosureFlatComponent } from '../self-disclosure-flat/self-disclosure-flat.component';
import { SelfDisclosureAddressComponent } from '../self-disclosure-address/self-disclosure-address.component';
import { SelfDisclosureEmploymentComponent } from '../self-disclosure-employment/self-disclosure-employment.component';
import { SelfDisclosureService } from '../../self-disclosure.service';
import { defaultDocumentsConfig } from '../../../config/documents.config';
import { isObject } from '../../../utils/type-guards';
import { FormFieldComponent } from '../../../components/legacy/form/form-field/form-field.component';
import { FlatSelectComponent } from '../../../components/legacy/form/controls/flat-select/flat-select.component';
import { AttachmentsComponent } from '../../../components/legacy/form/controls/attachment/attachments.component';
import { FormFieldLabelComponent } from '../../../components/legacy/form/form-field/form-field-label/form-field-label.component';
import { AppInputDirective } from '../../../components/legacy/form/controls/input/input.directive';
import { SelfDisclosureUniqueFieldsService } from '../../self-disclosure-unique-fields.service';

const BOOLEAN_OPTIONS = [
  { name: 'general.yes_l', value: true },
  { name: 'general.no_l', value: false }
];

@Component({
  selector: 'app-self-disclosure-form',
  templateUrl: './self-disclosure-form.component.html',
  styleUrls: ['./self-disclosure-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelfDisclosureFormComponent),
      multi: true
    },
    SelfDisclosureUniqueFieldsService
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    FormFieldComponent,
    FlatSelectComponent,
    AppInputDirective,
    AttachmentsComponent,
    SelfDisclosureFlatComponent,
    SelfDisclosurePersonComponent,
    SelfDisclosurePersonListComponent,
    SelfDisclosureChildListComponent,
    SelfDisclosureAddressComponent,
    SelfDisclosureEmploymentComponent,
    NgStyle,
    FormFieldLabelComponent
  ]
})
export class SelfDisclosureFormComponent
  implements OnInit, ControlValueAccessor
{
  #destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);
  private selfDisclosureService = inject(SelfDisclosureService);
  private registry = inject(SelfDisclosureUniqueFieldsService);

  readonly selfDisclosureModel = input<SelfDisclosureModel>(undefined);

  readonly userData = input<PropertySearcherUser | null>();
  /** The WBS is no longer part of the profile, so its documents come separately. */
  readonly wbsDocuments = input<Attachment[]>([]);
  readonly propertySearcher = input<PropertySearcher>(undefined);
  readonly constants = input<Constants>(undefined);
  readonly formValiditySubject = input<Subject<boolean>>(undefined);

  readonly validityChange = output<boolean>();

  public form = this.fb.group({
    id: this.fb.control(''),
    feedbackEmail: this.fb.control(''),
    questions: this.fb.array<FormControl<SelfDisclosureQuestion>>([]),
    documents: this.fb.control<string[]>([]),
    uploadedDocuments: this.fb.control<Attachment[]>([]),
    confirmations: this.fb.control([]),
    description: this.fb.control(''),
    propertySearcherComment: this.fb.control(''),
    emailMustBeUnique: this.fb.control(false),
    phoneMustBeUnique: this.fb.control(false)
  });
  public value: SelfDisclosureModel;

  public acceptedDocumentsTypes = defaultDocumentsConfig.allAcceptedTypes;
  public documentsMaxSize = 1024 * 1024 * 20;

  private onChange = (value: unknown) => value;
  private onTouch = () => null;

  public ngOnInit() {
    const formValiditySubject = this.formValiditySubject();
    if (formValiditySubject) {
      formValiditySubject
        .asObservable()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(isValid => {
          if (isValid) {
            this.checkAndValidity(this.questionsFormArray);
            this.questionsFormArray.updateValueAndValidity({ emitEvent: true });
            this.form.updateValueAndValidity({ emitEvent: true });
          }
        });
    }

    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((value: SelfDisclosureModel) => {
        this.value = value;
        this.onChange(this.value);
        this.onTouch();
      });

    this.form.statusChanges
      .pipe(delay(0), takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => {
        this.validityChange.emit(value === 'VALID');
      });

    this.form.controls.emailMustBeUnique.valueChanges
      .pipe(
        filter(value => !!value),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.registry.enableValidation('email');
      });

    this.form.controls.phoneMustBeUnique.valueChanges
      .pipe(
        filter(value => !!value),
        take(1),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => {
        this.registry.enableValidation('phone');
      });
  }

  private checkAndValidity(array: FormArray) {
    array.controls.forEach((control: FormGroup) => {
      /**
       * We need to set the touched value of all invalid control fields to show the error message
       * when the save button is clicked.
       */
      const answer = control.get('answer');
      if (
        (answer as FormArray).errors &&
        (answer as FormArray).errors['listIsEmpty']
      ) {
        this.checkAndMarkControl(answer as FormControl);
      } else if ((answer as FormArray).length > 0) {
        this.checkAndValidity(answer as FormArray);
      } else if (!(answer as FormGroup).controls) {
        if (
          control.value.type === 'DOCUMENT' ||
          control.value.type === 'DOCUMENTS'
        ) {
          control.get('uploads').setErrors({ required: true });
          this.checkAndMarkControl(control.get('uploads') as FormControl);
          control.updateValueAndValidity();
        } else {
          this.checkAndMarkControl(answer as FormControl);
        }
      } else if ((answer as FormGroup).controls) {
        const answerControls = (answer as FormGroup).controls;
        Object.keys(answerControls).forEach(key => {
          const formControl = answerControls[key] as FormControl;
          this.checkAndMarkControl(formControl);
        });
      }

      const confirmations = control.get('confirmations') as FormArray;
      confirmations?.controls?.forEach(item => {
        this.checkAndMarkControl(item as FormControl);
      });

      // recursive call to check validity for sub-questions as well
      const controlsSubQuestions = control.get('subQuestions') as FormArray;
      if (controlsSubQuestions?.length > 0) {
        this.checkAndValidity(controlsSubQuestions);
      }
    });
  }

  private checkAndMarkControl(formControl: FormControl) {
    if (formControl.status === 'INVALID') {
      formControl.markAsDirty();
      formControl.markAsTouched();
      formControl.updateValueAndValidity({ emitEvent: true });
    }
  }

  public get questionsFormArray() {
    return this.form.get('questions') as FormArray;
  }

  private get confirmationFormArray() {
    return this.form.get('confirmations') as FormArray;
  }

  public getQuestionsForm(index: number) {
    return this.questionsFormArray.controls[index] as FormGroup;
  }

  public isValid(form: FormGroup) {
    return form.invalid && form.touched && form?.value?.answer?.length === 0;
  }

  public writeValue(value: SelfDisclosureModel) {
    const model: SelfDisclosureModel = {
      ...value,
      questions: value.questions.map(q => ({
        ...q,
        subQuestions: q.subQuestions.map(sq => ({ ...sq }))
      }))
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.confirmationFormArray.value.forEach(() =>
      this.confirmationFormArray.removeAt(0)
    );

    // create the FormArray for questions based on SD model
    const selfDisclosureModel = this.selfDisclosureModel();
    this.form.setControl(
      'questions',
      this.selfDisclosureService.createQuestionFormArray(
        model,
        selfDisclosureModel.questions
      )
    );

    // pre-fill values
    if (
      !selfDisclosureModel.isAnswered &&
      this.userData() &&
      this.propertySearcher()
    ) {
      this.preFillQuestionFields(model.questions, this.questionsFormArray);
    }

    this.value = model || ([] as SelfDisclosureModel);

    if (selfDisclosureModel.isAnswered) {
      this.form.patchValue({
        questions: selfDisclosureModel.questions,
        uploadedDocuments: selfDisclosureModel.uploadedDocuments,
        propertySearcherComment: selfDisclosureModel.propertySearcherComment,
        description: this.value.description,
        documents: this.value.documents,
        emailMustBeUnique: selfDisclosureModel.emailMustBeUnique,
        phoneMustBeUnique: selfDisclosureModel.phoneMustBeUnique
      });
    } else {
      this.form.patchValue({
        questions: this.value.questions,
        documents: this.value.documents,
        description: this.value.description,
        uploadedDocuments: this.value.uploadedDocuments || [],
        emailMustBeUnique: this.value.emailMustBeUnique,
        phoneMustBeUnique: this.value.phoneMustBeUnique
      });
    }
  }

  private preFillQuestionFields(
    questionList: SelfDisclosureQuestion[],
    formArray: FormArray
  ) {
    questionList.forEach((question, index) => {
      let value;
      switch (question.title) {
        case 'SELF_DISCLOSURE_PERSONAL_INFO_L': {
          value = this.getPersonalUserData(this.userData().profile);
          if (question.subQuestions) {
            this.preFillQuestionFields(
              question.subQuestions,
              formArray.at(index).get('subQuestions') as FormArray
            );
          }
          break;
        }
        case 'SELF_DISCLOSURE_FLAT_VISITED_L': {
          value = this.getFlatData(this.propertySearcher().property);
          break;
        }
        case 'SELF_DISCLOSURE_CURRENT_ADDRESS_L': {
          value = this.getAddressData(this.userData());
          break;
        }
        case 'SELF_DISCLOSURE_EMPLOYMENT_L': {
          value = this.getEmployerData(this.userData().profile);
          break;
        }
        case 'SELF_DISCLOSURE_DOCUMENT_CREDIT_RATING': {
          const creditReport = this.getDocument(
            this.userData().profile.attachments,
            'CREDIT_REPORT'
          );
          if (isObject(creditReport)) {
            formArray.at(index).get('uploads').patchValue(creditReport);
            question.uploads = [creditReport];
          }
          break;
        }
        case 'SELF_DISCLOSURE_DOCUMENT_INCOME': {
          const incomeStatements = this.getDocuments(
            this.userData().profile.attachments,
            'INCOME_STATEMENT'
          );
          if (incomeStatements.length) {
            formArray.at(index).get('uploads').patchValue(incomeStatements);
            question.uploads = incomeStatements;
          }
          break;
        }
        case 'SELF_DISCLOSURE_DOCUMENT_CERTIFICATE_OF_STUDY': {
          const certificateOfStudy = this.getDocuments(
            this.userData().profile.attachments,
            'CERTIFICATE_OF_STUDY'
          );
          if (certificateOfStudy.length) {
            formArray.at(index).get('uploads').patchValue(certificateOfStudy);
            question.uploads = certificateOfStudy;
          }
          break;
        }
        case 'SELF_DISCLOSURE_DOCUMENT_WBS_L': {
          const wbsDocs = this.wbsDocuments();
          value = true;
          formArray.at(index).get('uploads').patchValue(wbsDocs);
          question.uploads = wbsDocs;
          break;
        }
      }
      if (value) {
        formArray.at(index).get('answer').patchValue(value);
        question.answer = value;
      }
    });
  }

  private getDocument(attachments: Attachment[], type: string) {
    return attachments.find(attachment => attachment.type === type);
  }

  private getDocuments(attachments: Attachment[], type: string) {
    return attachments.filter(attachment => attachment.type === type);
  }

  private getPersonalUserData(userProfile: PropertySearcherUserProfile) {
    const {
      firstname = null,
      name = null,
      dateOfBirth = null
    } = userProfile || {};
    return {
      firstName: firstname,
      lastName: name,
      birthDate: dateOfBirth
    };
  }

  private getAddressData(user: PropertySearcherUser) {
    return {
      city: user.address ? user.address.city : null,
      street: user.address ? user.address.street : null,
      houseNumber: user.address ? user.address.houseNumber : null,
      zipCode: user.address ? user.address.zipCode : null,
      phone: user.profile.phone,
      email: user.email
    };
  }

  private getEmployerData(userProfile: PropertySearcherUserProfile) {
    return {
      job:
        userProfile && userProfile.profession
          ? userProfile.profession.subType
          : null,
      netIncome:
        userProfile && userProfile.profession
          ? userProfile.profession.income
          : null
    };
  }

  private getFlatData(property: Property) {
    const { totalRent, rooms, size } = property;
    const address = property.address;
    return {
      city: address ? address.city : null,
      street: address ? address.street : null,
      numberOfRooms: rooms,
      sizeInSm: size,
      totalRent: totalRent
    };
  }

  public registerOnChange(fn) {
    this.onChange = fn;
  }

  public registerOnTouched(fn) {
    this.onTouch = fn;
  }

  public isBoolean(question: SelfDisclosureQuestion) {
    return question.type === SelfDisclosureQuestionType.BOOLEAN;
  }

  public showBooleanOrSelect(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      (question.type === SelfDisclosureQuestionType.BOOLEAN ||
        question.type === SelfDisclosureQuestionType.SELECT)
    );
  }

  public showAddress(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.ADDRESS
    );
  }

  public showFlat(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.FLAT
    );
  }

  public showPerson(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.PERSON
    );
  }

  public showPersonList(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.PERSONS
    );
  }

  public showChildren(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.CHILDREN
    );
  }

  public showBirthdate(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.title === 'SELF_DISCLOSURE_CHILDREN_ADULT_L'
    );
  }

  public showEmployment(question: SelfDisclosureQuestion) {
    return (
      question.showSelfDisclosureQuestions &&
      question.type === SelfDisclosureQuestionType.EMPLOYMENT
    );
  }

  public options(question, isBoolean) {
    return isBoolean ? BOOLEAN_OPTIONS : [];
  }

  /**
   * Only show hit for positive answer for now.
   * @param index
   */
  public showHint(index: number) {
    const control = this.getQuestionsForm(index).value;

    const selfDisclosureModel = this.selfDisclosureModel();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      selfDisclosureModel.questions[index] &&
      selfDisclosureModel.questions[index].commentAllowed &&
      control &&
      control.answer &&
      (!Array.isArray(control.answer) || control.answer.length)
    );
  }

  public showUpload(index: number) {
    const control = this.getQuestionsForm(index).value;

    const selfDisclosureModel = this.selfDisclosureModel();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      selfDisclosureModel.questions[index] &&
      selfDisclosureModel.questions[index].uploadAllowed &&
      control &&
      control.answer
    );
  }
}
